/**
 * 장비 하나의 촬영 날짜와 날짜별 3D 결과 — 3D 분석 화면과 비교 창이 같이 쓴다 (같은 쿼리 키라 한 번만 받는다).
 */
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { GrowthRecord } from '@grosync/common';
import { fetchGrowthRecords } from '../../api/growth';
import { fetchGrowth3dResult, type Growth3dResult } from './api';
import { isLrmInFlight } from './progress';

/** 처리 중인 사진이 있으면 이 간격으로 다시 읽는다 — '3D 처리 중' → 결과가 저절로 바뀐다 */
const IN_FLIGHT_REFETCH_MS = 20_000;

export interface ResultState {
  data: Growth3dResult | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export interface DeviceShots {
  isLoading: boolean;
  isError: boolean;
  /** 오래된 날짜부터 · 사진 있는 기록만 (3D 는 사진 옆 파일이다) */
  shots: GrowthRecord[];
  /** 기록 id → 그날 3D 결과 json 상태. 404 = data null ('3D 처리 전') */
  states: Map<string, ResultState>;
  /** 기록 id → 그날 3D 결과 (없거나 받는 중이면 null/undefined) */
  results: Map<string, Growth3dResult | null | undefined>;
}

export function useDeviceShots(deviceId: string): DeviceShots {
  const recordsQ = useQuery({
    queryKey: ['growth-records', deviceId],
    queryFn: () => fetchGrowthRecords({ deviceId, pageSize: 60 }),
    enabled: !!deviceId,
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((r) => isLrmInFlight(r)) ? IN_FLIGHT_REFETCH_MS : false,
  });
  const shots = useMemo<GrowthRecord[]>(
    () =>
      (recordsQ.data?.items ?? [])
        .filter((r) => r.photoPath)
        .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)),
    [recordsQ.data],
  );
  // 날짜별 결과 json — 하루 1개라 작은 파일을 날짜 수만큼 받는다
  const resultQs = useQueries({
    queries: shots.map((r) => ({
      queryKey: ['growth3d-result', r.photoPath],
      queryFn: () => fetchGrowth3dResult(r.photoPath!),
      staleTime: 60_000,
      refetchInterval: isLrmInFlight(r) ? IN_FLIGHT_REFETCH_MS : (false as const),
    })),
  });
  const { states, results } = useMemo(() => {
    const st = new Map<string, ResultState>();
    const rs = new Map<string, Growth3dResult | null | undefined>();
    shots.forEach((r, i) => {
      const q = resultQs[i];
      st.set(r.id, {
        data: q?.data,
        isLoading: !!q?.isLoading,
        isError: !!q?.isError,
        error: q?.error ?? null,
      });
      rs.set(r.id, q?.data);
    });
    return { states: st, results: rs };
  }, [shots, resultQs]);
  return {
    isLoading: recordsQ.isLoading,
    isError: recordsQ.isError,
    shots,
    states,
    results,
  };
}

/** 마지막 촬영일부터 거꾸로 `days` 일 안의 기록만 */
export function withinDays(shots: GrowthRecord[], days: number): GrowthRecord[] {
  const last = shots[shots.length - 1]?.recordedAt.slice(0, 10);
  if (!last) return shots;
  const from = new Date(Date.parse(`${last}T00:00:00Z`) - (days - 1) * 86_400_000)
    .toISOString()
    .slice(0, 10);
  return shots.filter((r) => r.recordedAt.slice(0, 10) >= from);
}
